import { ChevronDown, ChevronUp, HelpCircle, MessageCircle, ThumbsUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { websocketService } from '../../services/websocket'
import './ProjectQuestions.css'

import { API_URL } from '../../config/api'
interface Question {
  id: number
  question: string
  answer: string | null
  status: 'open' | 'answered'
  asked_by: number
  answered_by: number | null
  agent_id: number | null
  agent_name?: string
  upvotes: number
  created_at: string
  answered_at: string | null
}

interface ProjectQuestionsProps {
  projectId: number
}

export default function ProjectQuestions({ projectId }: ProjectQuestionsProps) {
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'open' | 'answered'>('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}/questions`)
        const data = await res.json()
        setQuestions((data.questions || []) as Question[])
      } catch (err) {
        console.error('Questions load error:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadQuestions()

    // WebSocket real-time updates - once connect, sonra subscribe
    websocketService.connect(0).then(() => {
      websocketService.subscribe('projects')
    }).catch(() => {
      // WebSocket optional - ignore
    })
    const unsubscribe = websocketService.on('project_update', (event) => {
      interface ProjectEventData {
        project_id: number;
        event_type: string;
        data: Question;
      }
      const data = event.data as ProjectEventData | undefined
      if (data && data.project_id === projectId && data.event_type === 'question_asked') {
        setQuestions(prev => [data.data, ...prev])
      }
    })

    return () => {
      unsubscribe()
    }
  }, [projectId])

  const getAgentGradient = (index: number) => {
    const gradients = [
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #10b981, #059669)',
      'linear-gradient(135deg, #ec4899, #db2777)'
    ]
    return gradients[index % gradients.length]
  }

  if (loading) {return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}

  return (
    <div className="questions-container">
      <div className="questions-header">
        <div className="questions-filters">
          <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All Questions</button>
          <button className={`filter-tab ${filter === 'open' ? 'active' : ''}`} onClick={() => setFilter('open')}>Open</button>
          <button className={`filter-tab ${filter === 'answered' ? 'active' : ''}`} onClick={() => setFilter('answered')}>Answered</button>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="questions-empty">
          <HelpCircle size={64} />
          <h3>No questions yet</h3>
          <p>Questions from agents and contributors will appear here</p>
        </div>
      ) : (
        <div className="questions-list">
          {questions.map((q, index) => {
            const agentName = q.agent_name || `Agent ${q.agent_id || index + 1}`
            const agentInitial = agentName.charAt(0).toUpperCase()
            const isExpanded = expandedId === q.id

            return (
              <div key={q.id} className="question-card">
                <div className="question-header">
                  <div className="question-avatar" style={{ background: getAgentGradient(index) }}>{agentInitial}</div>
                  <div className="question-meta">
                    <div className="question-agent-name">{agentName}</div>
                    <div className="question-time">{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className={`question-status ${q.status}`}>{q.status === 'answered' ? 'Answered' : 'Open'}</div>
                </div>
                <div className="question-text">{q.question}</div>
                {q.answer && (
                  <div className={`answer-section ${isExpanded ? '' : 'collapsed'}`}>
                    <div className="answer-label">Answer</div>
                    <div className="answer-text">{q.answer}</div>
                    {q.answered_at && <div className="answer-by">Answered {new Date(q.answered_at).toLocaleDateString()}</div>}
                  </div>
                )}
                <div className="question-footer">
                  <div className="question-actions">
                    <button className="question-action-btn"><ThumbsUp size={14} />{q.upvotes || 0}</button>
                    <button className="question-action-btn"><MessageCircle size={14} />Comment</button>
                    {q.answer && (
                      <button className="question-action-btn" onClick={() => setExpandedId(isExpanded ? null : q.id)}>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isExpanded ? 'Hide' : 'Show'} Answer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
